import type { CSSProperties } from 'react';

import { space, type SpaceScale } from './tokens';

/**
 * Padding props — the shared padding contract of the layout primitives, alongside the layout props
 * (ADR 0072, extending ADR 0040). `Box` and `Flex` take all of them; `Grid` and `Container` take only
 * the block-axis ones (`BlockPaddingProps`), because their inline edge is the page column.
 *
 * Values are `SpaceScale` token keys only — never a raw length. Each axis is gated separately: the
 * primitive emits `data-padding="block"`, `"inline"` or both, and `padding.module.css` sets an axis
 * only when its word is present. So an element that sets no padding prop declares no padding, a
 * consumer `className` keeps winning, and block padding on a `Container` leaves its inline padding alone.
 *
 * **Cascade order** (highest priority last): `p` → `px` / `py` → `pt` / `pr` / `pb` / `pl`.
 */
export interface PaddingProps {
    /**
     * Padding on all four sides.
     * Accepts a `SpaceScale` token key — maps to `var(--space-{n})`.
     */
    p?: SpaceScale;
    /**
     * Horizontal padding (left + right).
     * Overrides `p` for left and right sides.
     */
    px?: SpaceScale;
    /**
     * Vertical padding (top + bottom).
     * Overrides `p` for top and bottom sides.
     */
    py?: SpaceScale;
    /**
     * Top padding.
     * Overrides `p` and `py`.
     */
    pt?: SpaceScale;
    /**
     * Right padding.
     * Overrides `p` and `px`.
     */
    pr?: SpaceScale;
    /**
     * Bottom padding.
     * Overrides `p` and `py`.
     */
    pb?: SpaceScale;
    /**
     * Left padding.
     * Overrides `p` and `px`.
     */
    pl?: SpaceScale;
}

/** The block-axis padding props — what `Grid` and `Container` accept. */
export type BlockPaddingProps = Pick<PaddingProps, 'py' | 'pt' | 'pb'>;

/** Every key of `PaddingProps`, so a primitive can split them off `...rest`. */
export const PADDING_PROP_KEYS = [
    'p',
    'px',
    'py',
    'pt',
    'pr',
    'pb',
    'pl',
] as const satisfies readonly (keyof PaddingProps)[];

/** What a primitive spreads onto its root: the gating attribute and the custom properties. */
export interface ResolvedPadding {
    /** `data-padding` — `'block'`, `'inline'` or `'block inline'`; `undefined` (not rendered) when no prop is set. */
    attrs: { 'data-padding'?: string };
    /** `--padding-*` custom properties — merge into the element's `style`. */
    vars: CSSProperties;
}

/**
 * Splits the padding props off an arbitrary props object and resolves them to the attribute and
 * custom properties `padding.module.css` consumes. Everything else comes back untouched in `rest`.
 *
 * An axis that is set emits **both** of its sides, an unset one as `--space-0`: custom properties
 * inherit, so an omitted side would pick up an ancestor primitive's value instead.
 *
 * @example
 * const padding = resolvePaddingProps(others);
 * const layout = resolveLayoutProps(padding.rest);
 * <Tag {...padding.attrs} {...layout.attrs} className={clsx(styles.box, paddingStyles.padding, …)} style={{ ...padding.vars, ...layout.vars, ...style }} {...layout.rest} />
 */
export function resolvePaddingProps<P extends Partial<PaddingProps>>(
    props: P
): ResolvedPadding & { rest: Omit<P, keyof PaddingProps> } {
    const { p, px, py, pt, pr, pb, pl, ...rest } = props;

    const block = [p, py, pt, pb].some((value) => value != null);
    const inline = [p, px, pr, pl].some((value) => value != null);

    const vars: Record<string, string | undefined> = {};
    const words: string[] = [];
    if (block) {
        vars['--padding-top'] = space(pt ?? py ?? p ?? 0);
        vars['--padding-bottom'] = space(pb ?? py ?? p ?? 0);
        words.push('block');
    }
    if (inline) {
        vars['--padding-right'] = space(pr ?? px ?? p ?? 0);
        vars['--padding-left'] = space(pl ?? px ?? p ?? 0);
        words.push('inline');
    }

    return {
        attrs: { 'data-padding': words.length ? words.join(' ') : undefined },
        vars: vars as CSSProperties,
        rest,
    };
}
